import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Building2, Users, MapPin, Check, Send, Loader2,
  Briefcase, Globe, Phone, Mail, ArrowLeft, BadgeCheck
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const benefits = [
  { icon: MapPin, title: "Multi-Location Access", description: "Give your team access to verified workspaces across multiple cities" },
  { icon: Users, title: "Team Management", description: "Centralized billing and usage tracking for your entire organization" },
  { icon: BadgeCheck, title: "Priority Listings", description: "Featured placement for all your company's venues" },
  { icon: Globe, title: "Custom Solutions", description: "Tailored packages to match your company's specific needs" },
];

const companySizes = [
  { value: "1-10", label: "1-10 employees" },
  { value: "11-50", label: "11-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "201-500", label: "201-500 employees" },
  { value: "500+", label: "500+ employees" },
];

const venueCountOptions = [
  { value: "1-5", label: "1-5 venues" },
  { value: "6-20", label: "6-20 venues" },
  { value: "21-50", label: "21-50 venues" },
  { value: "50+", label: "50+ venues" },
];

export default function CorporatePage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    company_size: "",
    venue_count: "",
    message: "",
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.contact_name || !formData.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/corporate/inquiry`, formData);
      setSubmitted(true);
      toast.success("Inquiry submitted successfully!");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-surface flex flex-col">
        <Header />
        <main className="flex-1 pt-24 px-4 max-w-lg mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Check className="w-10 h-10 text-green-600" />
          </motion.div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-3">Thank You!</h1>
          <p className="text-gray-600 mb-6">
            We've received your corporate inquiry. A member of our team will be in touch 
            within 2 business days to discuss your requirements.
          </p>
          <button
            onClick={() => navigate("/")}
            className="btn-primary"
            data-testid="back-to-home"
          >
            Back to Home
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <SEO 
        title="Corporate Workspace Solutions - Multi-Venue Packages"
        description="Corporate workspace solutions for businesses with multiple venues or distributed teams. Get volume discounts, priority listings, and custom packages for your organization."
        keywords="corporate workspace, business workspace, multi-venue listing, team workspace access, corporate coworking, business remote work"
        canonical="/corporate"
      />
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16 px-4 relative overflow-hidden">
          {/* Strider Logo Watermark */}
          <div 
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage: 'url(/strider-logo-bg.png)',
              backgroundSize: '450px',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'brightness(2)'
            }}
          />
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Building2 className="w-8 h-8 text-brand-light" />
              <span className="text-brand-light font-medium">Corporate Solutions</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Workspace Solutions for Your Business
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Whether you manage multiple venues or need workspace access for your distributed team, 
              we offer tailored corporate packages with volume discounts.
            </p>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-12 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-5 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center mb-3">
                    <benefit.icon className="w-5 h-5 text-brand" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">Get in Touch</h2>
              <p className="text-gray-600">
                Tell us about your business and we'll create a custom package that works for you.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Company Name */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Company Name *
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => updateField("company_name", e.target.value)}
                      placeholder="Your company name"
                      className="w-full pl-11 pr-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      data-testid="company-name-input"
                    />
                  </div>
                </div>

                {/* Contact Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Your Name *
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.contact_name}
                      onChange={(e) => updateField("contact_name", e.target.value)}
                      placeholder="Full name"
                      className="w-full pl-11 pr-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      data-testid="contact-name-input"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Work Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      placeholder="you@company.com"
                      className="w-full pl-11 pr-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                      placeholder="+44..."
                      className="w-full pl-11 pr-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                {/* Company Size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Company Size
                  </label>
                  <select
                    value={formData.company_size}
                    onChange={(e) => updateField("company_size", e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    data-testid="company-size-select"
                  >
                    <option value="">Select size</option>
                    {companySizes.map((size) => (
                      <option key={size.value} value={size.value}>{size.label}</option>
                    ))}
                  </select>
                </div>

                {/* Venue Count */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Number of Venues
                  </label>
                  <select
                    value={formData.venue_count}
                    onChange={(e) => updateField("venue_count", e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    data-testid="venue-count-select"
                  >
                    <option value="">Select range</option>
                    {venueCountOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Tell us about your needs
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => updateField("message", e.target.value)}
                    placeholder="What are you looking for? Multiple venue listings? Team workspace access? Custom features?"
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand resize-none"
                    data-testid="message-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full mt-6 btn-primary py-3 flex items-center justify-center gap-2"
                data-testid="submit-inquiry"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Inquiry
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                By submitting this form, you agree to our{" "}
                <a href="/privacy" className="text-brand hover:underline">Privacy Policy</a>.
              </p>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
